import React, { useState, useEffect, useCallback } from "react";
import cn from "classnames";
import styles from "./ProspectDetail.module.sass";
import { useLocation, useParams, Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";

import NameAndDescription from "./NameAndDescription";
import MemosAndNotes from "./MemosAndNotes";
import DocumentsAndFiles from "./DocumentsAndFiles";

import Card from "../../components/Card";
import Dropdown from "../../components/Dropdown";
import LetterAvatar from "../../components/LetterAvatar";
import Icon from "../../components/Icon";
import Actions from "../../components/Actions";

import { getProspectById, updateProspect, getCommercials, assignCommercialToProspect } from "../../services/prospect.service";
import { convertProspectToStudent } from "../../services/createStudent.service";
import { showSuccess, showError } from "../../services/toast.service";
import { isAdmin } from "../../services/pocketbase";


// Options pour le menu d'actions - sera remplacé par actionsWithHandlers dans le composant
const navigation = ["Coordonnées", "Documents", "Mémos"];

const ProspectDetail = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const location = useLocation();
  const { id } = useParams();
  const { currentUser } = useAuth();
  const [currentProspect, setCurrentProspect] = useState(null);
  const [commercials, setCommercials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assigningCommercial, setAssigningCommercial] = useState(false);
  const [convertingProspect, setConvertingProspect] = useState(false);

  const refreshProspect = useCallback(async () => {
    try {
      if (id) {
        const prospectData = await getProspectById(id);
        setCurrentProspect(prospectData);
      }
    } catch (error) {
      console.error('Erreur lors de la récupération du prospect:', error);
    }
  }, [id]);

  useEffect(() => {
    const fetchProspect = async () => {
      try {
        if (location.state?.prospect) {
          setCurrentProspect(location.state.prospect);
        } else if (id) {
          await refreshProspect();
        }
        setLoading(false);
      } catch (error) {
        console.error('Erreur lors de la récupération du prospect:', error);
        setLoading(false);
      }
    };

    fetchProspect();
  }, [id, location.state, refreshProspect]);

  useEffect(() => {
    const fetchCommercials = async () => {
      try {
        const commercialsData = await getCommercials();
        setCommercials(commercialsData);
      } catch (error) {
        console.error('Erreur lors de la récupération des commerciaux:', error);
      }
    };

    fetchCommercials();
  }, []);

  const assignCommercial = async (commercialId) => {
    try {
      setAssigningCommercial(true);
      const commercial = commercials.find(c => c.id === commercialId);
      const isReassignment = currentProspect.expand?.assigned_to;
      
      await assignCommercialToProspect(currentProspect.id, commercialId);
      await refreshProspect();
      
      if (commercial) {
        const message = isReassignment 
          ? `Le prospect a été réassigné à ${commercial.name}`
          : `Le prospect a été assigné à ${commercial.name}`;
        showSuccess(message);
      }
      
      setAssigningCommercial(false);
    } catch (error) {
      console.error('Erreur lors de l\'assignation du commercial:', error);
      showError('Erreur lors de l\'assignation du commercial');
      setAssigningCommercial(false);
    }
  };

  const handleConvertToStudent = async () => {
    try {
      // Vérification uniquement pour les champs obligatoires, pas de contrôle sur le status
      if (!currentProspect.email || !currentProspect.last_name || !currentProspect.first_name) {
        showError('Le prospect doit avoir un email, un nom et un prénom pour être converti en étudiant.');
        return;
      }

      setConvertingProspect(true);
      const result = await convertProspectToStudent(currentProspect.id);

      await refreshProspect();
      showSuccess(`L'étudiant ${result.student.first_name} ${result.student.last_name} a été créé avec succès!`);
      setConvertingProspect(false);
    } catch (error) {
      console.error('Erreur lors de la conversion du prospect en étudiant:', error);
      showError(error.message);
      setConvertingProspect(false);
    }
  };

  // Définition des actions pour le composant Actions
  const actionsWithHandlers = [
    {
      title: "Créer un élève",
      action: () => handleConvertToStudent()
    }
  ];

  if (loading) {
    return <div className={styles.loading}>Chargement...</div>;
  }

  if (!currentProspect) {
    return <div className={styles.error}>Prospect non trouvé</div>;
  }

  return (
    <>
      <div className={styles.shop}>
        <Link className={cn("button-white", styles.button)} to="/admin/prospects_student">
          <Icon name="arrow-left" />
          Retour
        </Link>

        <div className={styles.background}>
          <img src="/images/content/bg-shop.jpg" alt="Background" />
        </div>

        <Card className={styles.card}>
          <div className={styles.profile}>
            <div className={styles.details}>
              <div className={styles.avatar}>
                <LetterAvatar
                  firstName={currentProspect.first_name}
                  lastName={currentProspect.last_name}
                  size={80}
                />
              </div>
              <div className={styles.wrap}>
                <div className={cn("h4", styles.man)}>{currentProspect.first_name} {currentProspect.last_name}</div>
                <div className={styles.login}>
                  {(currentProspect.expand?.desired_formations && Object.keys(currentProspect.expand.desired_formations).length > 0) ? 
                    currentProspect.expand.desired_formations.exact_title_of_qualification : 
                    (currentProspect.desired_formations?.exact_title_of_qualification || 'Non spécifié')}
                </div>
              </div>
            </div>

            <div className={styles.statusContainer}>
              <div className={styles.statusGroup}>
                <div className={styles.login}>Assigné à</div>
                {currentProspect.expand?.assigned_to && !isAdmin() ? (
                  <div className={cn(styles.statusBadge, 'status-type', 'status-blue-dark')}>
                    {currentProspect.expand.assigned_to.name}
                  </div>
                ) : (
                  <div className={styles.assignCommercial}>
                    {assigningCommercial ? (
                      <div className={cn(styles.statusBadge, 'status-type', 'status-gray-dark')}>
                        {currentProspect.expand?.assigned_to ? 'Changement...' : 'Assignation...'}
                      </div>
                    ) : (
                      <Dropdown
                        value={currentProspect.expand?.assigned_to ? 
                          currentProspect.expand.assigned_to.name : 
                          "Assigner un commercial"
                        }
                        options={commercials.map(commercial => commercial.name)}
                        setValue={(selectedName) => {
                          const selectedCommercial = commercials.find(c => c.name === selectedName);
                          if (selectedCommercial) {
                            assignCommercial(selectedCommercial.id);
                          }
                        }}
                      />
                    )}
                  </div>
                )}
              </div>
              <div className={styles.statusGroup}>
                <div className={styles.login}>Dernier contact</div>
                <div className={cn(styles.statusBadge, 'status-type', 'status-green-dark')}>
                  {currentProspect.last_contact_at ? (
                    (() => {
                      try {
                        const date = new Date(currentProspect.last_contact_at);
                        return date.toLocaleDateString('fr-FR', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric'
                        });
                      } catch (error) {
                        console.error('Erreur de format de date:', error);
                        return 'Date invalide';
                      }
                    })()
                  ) : 'Non renseigné'}
                </div>
              </div>

              <div className={styles.statusGroup}>
                <div className={styles.login}>Statut</div>
                <div className={cn(
                  styles.statusBadge,
                  'status-type',
                  currentProspect.status === 'Nouveau' ? 'status-blue' :
                  currentProspect.status === 'En cours' ? 'status-blue' :
                  currentProspect.status === 'Success' ? 'status-yellow' :
                  currentProspect.status === 'Hors cible' ? 'status-red' :
                  currentProspect.status === 'Créé' ? 'status-green-dark' :
                  'status-gray'
                )}>
                  {currentProspect.status}
                </div>
              </div>
            </div>
          </div>
          <div className={styles.control}>
            <div className={cn(styles.nav, styles.desktopNav)}>
              {navigation.map((x, index) => (
                <button
                  className={cn(styles.link, { [styles.active]: index === activeIndex })}
                  onClick={() => setActiveIndex(index)}
                  key={index}
                >
                  {x}
                </button>
              ))}
            </div>
            
            <div className={styles.actionContainer}>
              {convertingProspect ? (
                <div className={styles.loading}>Conversion en cours...</div>
              ) : (
                <Actions
                  className={styles.actions}
                  classActionsHead={styles.actionsHead}
                  items={actionsWithHandlers}
                  small
                />
              )}
            </div>

            {/* Navigation mobile */}
            <div className={styles.mobileNav}>
              <Dropdown
                className={styles.dropdown}
                value={navigation[activeIndex]}
                setValue={(value) => {
                  const index = navigation.findIndex(x => x === value);
                  if (index !== -1) setActiveIndex(index);
                }}
                options={navigation}
              />
            </div>
          </div>
        </Card>

        <div className={styles.wrap}>
          {activeIndex === 0 && <NameAndDescription prospect={currentProspect} />}
          {activeIndex === 1 && <DocumentsAndFiles prospect={currentProspect} onStatusUpdate={refreshProspect} />}
          {activeIndex === 2 && <MemosAndNotes prospect={currentProspect} onStatusUpdate={refreshProspect} />}
        </div>
      </div>
    </>
  );
};

export default ProspectDetail;